/* <cart-recommendations> — "You may also like" inside the cart drawer / cart page.
   - pulls Shopify product recommendations based on the first item in the cart
   - filters out anything already in the cart and anything sold out
   - every card is a one-tap Add: adds the first available variant via ajax and
     refreshes the drawer (no options step — kept deliberately quick)
   - re-runs whenever the cart changes */
if (!customElements.get('cart-recommendations')) {
  customElements.define(
    'cart-recommendations',
    class CartRecommendations extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('[data-cr-list]');
        this.limit = parseInt(this.dataset.limit || '4', 10);
        this.intent = this.dataset.intent || 'related';
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

        this.load();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this._unsub = subscribe(PUB_SUB_EVENTS.cartUpdate, (e) => {
            if (e && e.source === 'cart-recommendations') return;
            this.load();
          });
        }

        this.addEventListener('click', this.onClick.bind(this));
      }

      disconnectedCallback() {
        if (this._unsub) this._unsub();
      }

      async load() {
        try {
          const cart = await fetch(`${window.routes.cart_url || '/cart'}.js`).then((r) => r.json());
          if (!cart.items || !cart.items.length) return this.hide();

          const inCart = new Set(cart.items.map((i) => i.product_id));
          const seed = cart.items[0].product_id;

          const url = `/recommendations/products.json?product_id=${seed}&limit=${
            this.limit + inCart.size
          }&intent=${this.intent}`;
          const data = await fetch(url).then((r) => r.json());

          const products = (data.products || [])
            .filter((p) => !inCart.has(p.id))
            .filter((p) => (p.variants || []).some((v) => v.available))
            .slice(0, this.limit);

          if (!products.length) return this.hide();
          this.render(products);
          this.hidden = false;
        } catch (err) {
          console.error('[cart-recommendations]', err);
          this.hide();
        }
      }

      hide() {
        this.hidden = true;
        if (this.list) this.list.innerHTML = '';
      }

      money(cents) {
        const amount = (cents / 100).toFixed(2);
        return this.moneyFormat.replace(/\{\{\s*amount\s*\}\}/, amount);
      }

      render(products) {
        this.list.innerHTML = products
          .map((p) => {
            const img = p.featured_image
              ? `${p.featured_image.split('?')[0]}?width=160`
              : (p.images && p.images[0] ? `${p.images[0].split('?')[0]}?width=160` : '');
            const variant =
              (p.variants || []).find((v) => v.available) || (p.variants || [])[0];
            if (!variant) return '';
            const price = this.money(
              variant.price != null ? variant.price : p.price != null ? p.price : 0
            );
            return `
              <li class="cart-rec__item">
                <a class="cart-rec__media" href="${p.url}" tabindex="-1" aria-hidden="true">
                  ${img ? `<img src="${img}" alt="" width="80" height="80" loading="eager">` : ''}
                </a>
                <div class="cart-rec__info">
                  <a class="cart-rec__title" href="${p.url}">${p.title}</a>
                  <span class="cart-rec__price">${price}</span>
                </div>
                <button type="button" class="cart-rec__add button button--tertiary" data-cr-add="${variant.id}">
                  <span>${this.dataset.addLabel || 'Add'}</span>
                </button>
              </li>`;
          })
          .join('');
      }

      async onClick(evt) {
        const btn = evt.target.closest('[data-cr-add]');
        if (!btn) return;
        evt.preventDefault();
        if (btn.classList.contains('loading')) return;
        btn.classList.add('loading');

        const drawer = document.querySelector('cart-drawer');
        const bubble = document.querySelector('cart-notification');
        const host = drawer || bubble;

        const body = new FormData();
        body.append('id', btn.dataset.crAdd);
        body.append('quantity', 1);
        if (host && typeof host.getSectionsToRender === 'function') {
          body.append('sections', host.getSectionsToRender().map((s) => s.id).join(','));
          body.append('sections_url', window.location.pathname);
        }

        try {
          const res = await fetch(`${window.routes.cart_add_url}`, {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
            body,
          });
          const json = await res.json();
          if (json.status) throw new Error(json.description || 'add failed');

          if (host && typeof host.renderContents === 'function') {
            host.renderContents(json);
          } else if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-recommendations' });
          } else {
            window.location = window.routes.cart_url;
          }
        } catch (err) {
          console.error('[cart-recommendations] add', err);
          btn.classList.remove('loading');
        }
      }
    }
  );
}
